/**
 * Services API wrapper (v2 — Project Independence, DNKADM-0055).
 *
 * Services are a NEW v2 entity (handoff §5.5): the catalog that drives availability
 * and booking. A `v2_service` carries **variations** (each with an explicit, REQUIRED
 * `creditCost`), points at exactly one **credit bucket** (1:1; null = free),
 * declares how **staff** and **resources** are selected
 * (`NONE|MEMBER_SELECTS|ROUND_ROBIN`, §4.7), tags **categories**, chooses the
 * **locations** where it's offered, marks **taxability**, and supports per-staff
 * money-only **price exceptions** per variation. There is no v1 equivalent.
 *
 * This module owns the v2 entity CRUD + soft-delete/reactivate (§4.3), the four
 * assignment sub-resources (categories / locations / staff / resources get+set
 * round-trips), the variations CRUD, and the per-staff variation price exceptions.
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.5, §3.1 — standard REST + nested + soft-delete):**
 *  - `GET    /v2/admin/services?status=active|inactive|all` → {@link V2Service}`[]`.
 *  - `GET    /v2/admin/services/{id}` → one {@link V2Service}.
 *  - `POST   /v2/admin/services` ← {@link ServiceInput} → the created row.
 *  - `PUT    /v2/admin/services/{id}` ← {@link ServiceInput} → the updated row.
 *  - `POST   /v2/admin/services/{id}/deactivate` | `/reactivate` (§4.3).
 *  - `POST   /v2/admin/services/{id}/publish` → take a Draft live (DNKAPI-0261/0263).
 *  - `POST   /v2/admin/services/{id}/copy` → deep-clone into a new Draft (DNKAPI-0262/0264).
 *  - `GET/PUT /v2/admin/services/{id}/category-assignments` ← `{ categoryIds: [...] }`.
 *  - `GET/PUT /v2/admin/services/{id}/location-assignments` ← `{ locationIds: [...] }`.
 *  - `GET/PUT /v2/admin/services/{id}/staff-assignments`    ← `{ staffIds: [...] }`.
 *  - `GET/PUT /v2/admin/services/{id}/resource-assignments` ← `{ resourceIds: [...] }`.
 *  - `GET/POST/PUT/DELETE /v2/admin/services/{id}/variations[/{vid}]`.
 *  - `GET/PUT/DELETE /v2/admin/services/{id}/variations/{vid}/staff-prices[/{staffId}]`.
 *
 * **`v2_service` field set (handoff §5.5):** `name`, `description` (opt), `imageUrl`
 * (opt — assumed a PLAIN URL string; no upload pipeline, §Non-Goals), `creditBucketId`
 * (opt — 1:1; null = free), `staffAssignment` / `resourceAssignment`
 * (`NONE|MEMBER_SELECTS|ROUND_ROBIN`, §4.7 — **at least one ≠ NONE**), `taxable`
 * (boolean), **`allowGuests`** (boolean, default false) + **`maxGuests`** (per-reservation
 * cap; **`0` = unlimited**; default 0; DNKAPI-0145), `isActive`, `status`
 * (`DRAFT|ACTIVE|INACTIVE` lifecycle, DNKAPI-0261 — additive to `isActive`; a create
 * now yields a `DRAFT`).
 *
 * **`v2_service_variation` field set:** `serviceId`, `name`, `isDefault` (exactly one
 * per service), `durationMinutes`, `preBufferMinutes` (default 0), `postBufferMinutes`
 * (default 0), `price` (DECIMAL(10,2), pre-tax), **`noShowFee`** (nullable
 * dollar amount, 2dp; null means charge the booked variation price), **`creditCost`
 * (DECIMAL(8,2), REQUIRED — a variation cannot be saved without it).**
 *
 * **`v2_staff_variation_price`:** `staffId`, `variationId`, `price` — a per-staff
 * money override for a variation (money-only, never credits). Unique on
 * `(staff, variation)`.
 *
 * **Sub-resource wire shapes are ASSUMED standard REST** (handoff §10 — the live
 * OpenAPI "v2" group is authoritative when available; recorded in the DNKADM-0055
 * delivery's Notes). Every read is parsed DEFENSIVELY (mirrors `resources.ts` /
 * `staff.ts` / `locations.ts`): a missing/wrong-typed field degrades (an entry with no
 * usable `id` is dropped), never a throw.
 */
import { request } from './client';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';
import type { AssignmentMode } from '@/domain/v2Enums';
import { asLeadTimeMinutes } from '@/domain/leadTime';

/** v2 admin services base path (bearer surface). */
const PATH = '/v2/admin/services';

/** The three lifecycle states a service can be in (DNKAPI-0261). */
type ServiceStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';

/**
 * How payment is captured for a service at booking (DNKAPI-0271). Today's closed set
 * is `charge` (pay at booking — the historical default) and `card_on_file` (the card is
 * saved with no charge, e.g. a swing evaluation). Modeled as an EXTENSIBLE string enum
 * (`(string & {})`) so a future server value (e.g. `auth_hold`) is additive and
 * round-trips rather than breaking a two-item boolean assumption. The UI only ever sends
 * the two known values; the server applies the `charge` default when omitted.
 */
export type PaymentMode = 'charge' | 'card_on_file' | (string & {});

/**
 * The CLOSED presentation vocabulary for how a service's variations are rendered
 * (DNKAPI-0421 `VariationDisplay.ALL`, DNKADM-0267): exactly `duration` | `type` —
 * any other value is a server-side `400 INVALID_REQUEST`, so the UI select can only
 * ever produce these two. Presentation-only; never affects booking/pricing.
 */
export type VariationDisplay = 'duration' | 'type';

/**
 * One `v2_service` row (§5.5). `description`/`imageUrl` are optional strings (absent
 * when the server carries none — `exactOptionalPropertyTypes`: the key is omitted,
 * never `undefined`). `creditBucketId` is `null` for a free service (1:1 otherwise).
 * `staffAssignment`/`resourceAssignment` are the §4.7 enum (at least one ≠ NONE).
 * `variationCount` (active variations) and `minPrice`/`maxPrice` (the lowest/highest
 * active-variation base price; absent when there are none) are conveniences the list
 * shows when the server supplies them.
 */
export interface V2Service {
  id: string;
  name: string;
  /** The bound credit bucket (1:1), or `null` for a free service. */
  creditBucketId: string | null;
  /** How eligible STAFF are selected (§4.7). */
  staffAssignment: AssignmentMode;
  /** How eligible RESOURCES are selected (§4.7). */
  resourceAssignment: AssignmentMode;
  /** Service-level taxability (§5.5 — a single boolean; rates live on the location). */
  taxable: boolean;
  /** Whether bookings require operator confirmation (DNKAPI-0278; default `false`). */
  requiresConfirmation: boolean;
  /** Whether a member may bring guests on a reservation (DNKAPI-0145; default `false`). */
  allowGuests: boolean;
  /** Max guests per reservation (DNKAPI-0145); **`0` = unlimited**; default `0`. */
  maxGuests: number;
  /**
   * How payment is captured at booking (DNKAPI-0271; `charge` default / `card_on_file`).
   * Applies to the service and all its variations; extensible (see {@link PaymentMode}).
   */
  paymentMode: PaymentMode;
  /** Soft-delete flag (§4.3); `false` = deactivated. */
  isActive: boolean;
  /**
   * Lifecycle status (DNKAPI-0261/0263): `DRAFT` (created, never published — hidden from
   * customers, not purchasable/bookable), `ACTIVE` (published and live), `INACTIVE`
   * (deactivated/soft-removed). Independent of `isActive`: a Draft is still `isActive:true`
   * underneath; `INACTIVE` and `isActive:false` move together once a service has been
   * published. Always present on a GET response.
   */
  status: ServiceStatus;
  description?: string;
  /** A plain image URL (assumed — no upload pipeline, §Non-Goals). */
  imageUrl?: string;
  /**
   * How variations render (DNKADM-0267) — assigned only when the wire carries
   * exactly `duration`/`type`; absent otherwise so the form can omit-to-preserve.
   */
  variationDisplay?: VariationDisplay;
  /**
   * Featured-in-member-app presentation toggle (DNKADM-0267) — assigned only when
   * the wire carries a real boolean (absent ⇒ the form omits it, preserving).
   */
  featured?: boolean;
  /**
   * Per-service booking lead time in minutes (DNKADM-0385, DNKAPI-0539) — assigned
   * only when the wire carries a whole number `1..LEAD_TIME_MAX_MINUTES`; absent
   * (`null`/`0`/omitted on the wire) means the service INHERITS the tenant default.
   * When set it wins over the tenant value, including when it is shorter.
   */
  leadTimeMinutes?: number;
  /** The number of variations, when the list payload carries it. */
  variationCount?: number;
  /** The lowest active-variation base price (major units), when the list payload carries it. */
  minPrice?: number;
  /** The highest active-variation base price (major units), when the list payload carries it. */
  maxPrice?: number;
  /**
   * The assigned-categories summary carried inline on a LIST row (DNKADM-0079,
   * `HANDOFF-dink-api-service-row-categories`) — the source the grouped Services
   * tab buckets rows by, with NO per-service `category-assignments` fan-out.
   * Tri-state (absent ≠ empty): absent (`undefined`) = the field is not emitted yet
   * ⇒ the grouped view falls back to the flat table; an explicit `[]` = tagged with
   * no category ⇒ the row groups under "Uncategorized".
   */
  categories?: CategorySummary[];
}

/**
 * A minimal assigned-category summary (`{ id, name }`) carried inline on a service
 * LIST row (DNKADM-0079, `HANDOFF-dink-api-service-row-categories`). The grouped
 * Services tab buckets rows by these so it can render the per-category sections
 * without a per-row `category-assignments` fetch. (Distinct from
 * `serviceCategories.ts`'s `ServiceCategoryOption`, which is the CRUD/pick-list
 * shape.)
 */
interface CategorySummary {
  id: string;
  name: string;
}

/**
 * Create/update body for a service. `name` is required by the form; the assignment
 * modes + `taxable` are required (defaulted in the form). `creditBucketId`/`description`/
 * `imageUrl` are optional. `isActive` is NOT settable here — activation is the
 * dedicated deactivate/reactivate endpoints (§4.3).
 */
export interface ServiceInput {
  name: string;
  staffAssignment: AssignmentMode;
  resourceAssignment: AssignmentMode;
  taxable: boolean;
  /** Whether bookings require operator confirmation (DNKAPI-0278; required-with-default). */
  requiresConfirmation: boolean;
  /** Whether guests are allowed (DNKAPI-0145; required-with-default, like `taxable`). */
  allowGuests: boolean;
  /** Max guests per reservation (DNKAPI-0145); **`0` = unlimited**; default `0`; `>= 0`. */
  maxGuests: number;
  /**
   * Payment capture at booking (DNKAPI-0271; required-with-default `charge`, like
   * `taxable`). The form sends only the two known values ({@link PaymentMode}).
   */
  paymentMode: PaymentMode;
  /** The bound credit bucket (1:1), or `null` for a free service. */
  creditBucketId: string | null;
  description?: string;
  imageUrl?: string;
  /**
   * Nullable **preserve-on-null** on the wire (DNKADM-0267): sent only when the
   * operator has an actual selection; omitted otherwise (create → server defaults
   * `duration`; update → the stored value is preserved). Never send a blank.
   */
  variationDisplay?: VariationDisplay;
  /** Nullable preserve-on-null (DNKADM-0267) — omitted when the form has no value. */
  featured?: boolean;
  /**
   * Booking lead time override (DNKADM-0385). The billing-anchor sentinel: `0` =
   * clear (inherit the tenant default); `1`–`LEAD_TIME_MAX_MINUTES` = set; omit the
   * key to leave the stored value unchanged (create → inherit). Never send `null`.
   */
  leadTimeMinutes?: number;
}

/** Options for {@link listServices}. */
export interface ListServicesOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
  /**
   * Convenience flag (mirrors the spec's `listServices({ includeInactive? })`):
   * `true` ⇒ `status=all`. Ignored when an explicit `status` is also passed.
   */
  includeInactive?: boolean;
}

/**
 * One `v2_service_variation` row. `isDefault` marks the single default per service.
 * `creditCost` is REQUIRED on write (the form rejects a save without it); on read a
 * malformed/absent value degrades to 0 (the server is the authority). `price` is
 * pre-tax money in the tenant currency.
 */
export interface ServiceVariation {
  id: string;
  serviceId: string;
  name: string;
  isDefault: boolean;
  durationMinutes: number;
  preBufferMinutes: number;
  postBufferMinutes: number;
  price: number;
  /**
   * Per-variation no-show fee in dollar/major units, or `null` to charge the booked
   * variation price. Explicit `0` means no no-show charge.
   */
  noShowFee: number | null;
  /** REQUIRED credit cost (DECIMAL(8,2)) — explicit, no duration formula (§5.5). */
  creditCost: number;
}

/**
 * Create/update body for a variation. `creditCost` is REQUIRED — the form never
 * submits without it (the invariant is enforced before this is built).
 */
export interface ServiceVariationInput {
  name: string;
  isDefault: boolean;
  durationMinutes: number;
  preBufferMinutes: number;
  postBufferMinutes: number;
  price: number;
  /** Per-variation nullable no-show fee in dollar/major units; `0` is meaningful. */
  noShowFee: number | null;
  creditCost: number;
}

/**
 * One per-staff money override for a variation (`v2_staff_variation_price`). Unique
 * on `(staff, variation)`; money-only (never credits, §Non-Goals).
 */
export interface VariationStaffPrice {
  staffId: string;
  variationId: string;
  price: number;
}

/**
 * One per-day-of-week money override for a variation (DNKAPI-0168). Natural key is
 * `(variation, dayOfWeek)` — `dayOfWeek` is **0=Sun … 6=Sat** (the hours/schedules
 * convention). Money-only; `creditCost` is unaffected (§Non-Goals). `active` is the
 * soft-delete flag; `createdAt`/`updatedAt` are present only when the server emits
 * them (`exactOptionalPropertyTypes` — the key is omitted, never `undefined`).
 */
export interface WeeklyPrice {
  variationId: string;
  dayOfWeek: number;
  price: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * One per-date money override for a variation (DNKAPI-0168). Natural key is
 * `(variation, date)` — `date` is `YYYY-MM-DD`, the location-timezone calendar date.
 * Money-only; `creditCost` is unaffected. `active`/timestamps as {@link WeeklyPrice}.
 */
export interface DatePrice {
  variationId: string;
  date: string;
  price: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * The server-resolved effective price for a variation on a date (DNKAPI-0168). The
 * precedence (`Date > Staff > Day-of-week > Base`, most-specific-wins, NOT additive)
 * is resolved SERVER-SIDE — never computed client-side. `source` names which rung
 * won. The surface is NOT locked yet (forward-compatible); see {@link getEffectivePrice}.
 */
export interface EffectivePrice {
  price: number;
  source: 'date' | 'staff' | 'weekly' | 'base';
}

// --- Defensive parsing helpers (mirrors resources.ts / locations.ts) ------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** A finite number (number or numeric string), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/** A finite number ≥ 0 (number or numeric string), or the fallback. */
function asNonNegative(value: unknown, fallback: number): number {
  const n = asNumber(value);
  return n !== null && n >= 0 ? n : fallback;
}

/** A finite number ≥ 0 (number or numeric string), or null when absent/unparseable. */
function asNullableNonNegative(value: unknown): number | null {
  const n = asNumber(value);
  return n !== null && n >= 0 ? n : null;
}

/** A non-negative integer (number or numeric string), or the fallback. */
function asNonNegativeInt(value: unknown, fallback: number): number {
  const n = asNumber(value);
  return n !== null && Number.isInteger(n) && n >= 0 ? n : fallback;
}

/** Parse a raw assignment mode; anything unknown degrades to `NONE` (§4.7). */
function asAssignmentMode(value: unknown): AssignmentMode {
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    if (upper === 'MEMBER_SELECTS') return 'MEMBER_SELECTS';
    if (upper === 'ROUND_ROBIN') return 'ROUND_ROBIN';
  }
  return 'NONE';
}

/** Parse a raw lifecycle status; anything unrecognized degrades to `ACTIVE` (never crashes). */
function asServiceStatus(value: unknown): ServiceStatus {
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    if (upper === 'DRAFT') return 'DRAFT';
    if (upper === 'INACTIVE') return 'INACTIVE';
  }
  return 'ACTIVE';
}

/**
 * Parse one raw service row, or null when it has no usable `id` (an entry with no
 * selectable id is dropped rather than surfaced as a half-identified row). A missing
 * `name` degrades to an empty string. `creditBucketId` is null when absent/blank (a
 * free service). The assignment modes degrade to `NONE`. `description`/`imageUrl` are
 * assigned only when present (exactOptionalPropertyTypes).
 */
function parseService(raw: unknown): V2Service | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const service: V2Service = {
    id,
    name: asText(raw['name']) ?? '',
    // Nested v2 fk id (1:1 credit bucket) — numeric on the wire (DNKADM-0064).
    creditBucketId: asId(raw['creditBucketId']),
    staffAssignment: asAssignmentMode(raw['staffAssignment']),
    resourceAssignment: asAssignmentMode(raw['resourceAssignment']),
    taxable: asBoolean(raw['taxable'], false),
    requiresConfirmation: asBoolean(raw['requiresConfirmation'], false),
    // Guest policy (DNKAPI-0145): allowGuests defaults false; maxGuests defaults 0
    // (absent/non-numeric) and a negative clamps to 0 defensively (`0` = unlimited).
    allowGuests: asBoolean(raw['allowGuests'], false),
    maxGuests: asNonNegative(raw['maxGuests'], 0),
    // Payment capture (DNKAPI-0271): defaults to `charge` when absent/blank; an unknown
    // future value (e.g. `auth_hold`) is kept as-is so it round-trips (extensible enum).
    paymentMode:
      typeof raw['paymentMode'] === 'string' && raw['paymentMode'].length > 0
        ? (raw['paymentMode'] as PaymentMode)
        : 'charge',
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
    // Lifecycle status (DNKAPI-0261) — additive to `active`; drives the status chip
    // and the three-way Publish/Deactivate/Reactivate gating.
    status: asServiceStatus(raw['status']),
  };
  const description = asText(raw['description']);
  if (description !== null) service.description = description;
  const imageUrl = asText(raw['imageUrl']);
  if (imageUrl !== null) service.imageUrl = imageUrl;
  // Presentation metadata (DNKADM-0267): assigned only on an exact known value so
  // the form omits (preserves) anything else rather than echoing a wrong value.
  const variationDisplay = raw['variationDisplay'];
  if (variationDisplay === 'duration' || variationDisplay === 'type') {
    service.variationDisplay = variationDisplay;
  }
  if (typeof raw['featured'] === 'boolean') service.featured = raw['featured'];
  // Booking lead time (DNKADM-0385): only a real 1..MAX value is carried; null/0/absent
  // all mean "inherits the tenant default" and stay absent.
  const leadTimeMinutes = asLeadTimeMinutes(raw['leadTimeMinutes']);
  if (leadTimeMinutes !== null) service.leadTimeMinutes = leadTimeMinutes;
  const variationCount = asNumber(raw['variationCount']);
  if (variationCount !== null && variationCount >= 0) {
    service.variationCount = variationCount;
  }
  // Price range over the active variations (number or numeric string on the wire).
  const minPrice = asNumber(raw['minPrice']);
  if (minPrice !== null && minPrice >= 0) service.minPrice = minPrice;
  const maxPrice = asNumber(raw['maxPrice']);
  if (maxPrice !== null && maxPrice >= 0) service.maxPrice = maxPrice;
  // Inline assigned-categories summary (DNKADM-0079) — assigned only when the field
  // is present (absent ⇒ key omitted, exactOptionalPropertyTypes; absent ≠ empty).
  const categories = parseRowCategories(raw);
  if (categories !== undefined) service.categories = categories;
  return service;
}

/**
 * Parse the OPTIONAL inline assigned-categories summary off a service LIST row
 * (DNKADM-0079, `HANDOFF-dink-api-service-row-categories`), TOLERANTLY and
 * forward-compatibly (mirrors `parseRowLocations` in `locations.ts`). Accepts the
 * preferred `categories: { id, name }[]` (a bare `id` is tolerated too), OR an
 * ids-only `categoryIds: string[]` carried as `{ id, name: id }` for the grouped view
 * to resolve names from the loaded categories list (an unresolved id shows the id).
 * Entries with no usable `id` are dropped; a missing `name` degrades to the `id`.
 *
 * **CRITICAL absent-vs-empty distinction:** when NEITHER key is present the field is
 * UNKNOWN → returns `undefined` (the field is not emitted yet ⇒ the grouped view
 * falls back to the flat table); an explicit empty array is a real "tagged with no
 * category" → returns `[]` (the row groups under "Uncategorized"). Absent ≠ empty —
 * never a false "Uncategorized" because the field is merely absent.
 */
function parseRowCategories(raw: unknown): CategorySummary[] | undefined {
  if (!isObject(raw)) return undefined;
  const fromObjects = raw['categories'];
  if (Array.isArray(fromObjects)) {
    const out: CategorySummary[] = [];
    for (const entry of fromObjects) {
      const id = isObject(entry) ? asId(entry['id']) : asId(entry);
      if (id === null) continue;
      const name = isObject(entry) ? asText(entry['name']) : null;
      out.push({ id, name: name ?? id });
    }
    return out;
  }
  const fromIds = raw['categoryIds'];
  if (Array.isArray(fromIds)) {
    const out: CategorySummary[] = [];
    for (const entry of fromIds) {
      const id = asId(entry);
      if (id !== null) out.push({ id, name: id });
    }
    return out;
  }
  return undefined;
}

/**
 * Extract the services array TOLERANTLY from a successful body, returning `null` when
 * NEITHER a bare array NOR a known wrapper key (`services`/`content`) yields one. The
 * list read turns `null` into `[]` (a real first-run state, §8).
 */
function extractServices(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['services'])) return body['services'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/**
 * Parse one raw variation, or null when it has no usable `id`. `serviceId` falls back
 * to the empty string (the caller knows the owning service). Buffers default to 0;
 * `price`/`creditCost` default to 0 on a malformed read (the server is the authority;
 * the form enforces the real REQUIRED creditCost on write). `noShowFee` is nullable
 * dollar/major units; null means the backend charges the booked variation price.
 */
function parseVariation(raw: unknown): ServiceVariation | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;
  return {
    id,
    serviceId: asId(raw['serviceId']) ?? '',
    name: asText(raw['name']) ?? '',
    isDefault: asBoolean(raw['isDefault'], false),
    durationMinutes: asNonNegativeInt(raw['durationMinutes'], 0),
    preBufferMinutes: asNonNegativeInt(raw['preBufferMinutes'], 0),
    postBufferMinutes: asNonNegativeInt(raw['postBufferMinutes'], 0),
    price: asNonNegative(raw['price'], 0),
    noShowFee: asNullableNonNegative(raw['noShowFee']),
    creditCost: asNonNegative(raw['creditCost'], 0),
  };
}

/** Parse one raw per-staff variation price, or null without a usable `staffId`. */
function parseStaffPrice(raw: unknown): VariationStaffPrice | null {
  if (!isObject(raw)) return null;
  const staffId = asId(raw['staffId']);
  if (staffId === null) return null;
  const price = asNumber(raw['price']);
  if (price === null || price < 0) return null;
  return {
    staffId,
    variationId: asId(raw['variationId']) ?? '',
    price,
  };
}

/**
 * Parse one raw per-day-of-week price (DNKAPI-0168), or null when unusable — a
 * `dayOfWeek` outside the integer range 0–6, or a missing/negative `price`, drops the
 * entry (a malformed override is not usable). `active` defaults true; `createdAt`/
 * `updatedAt` are assigned only when present (`exactOptionalPropertyTypes`).
 */
function parseWeeklyPrice(raw: unknown): WeeklyPrice | null {
  if (!isObject(raw)) return null;
  const dayOfWeek = asNumber(raw['dayOfWeek']);
  if (dayOfWeek === null || !Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) {
    return null;
  }
  const price = asNumber(raw['price']);
  if (price === null || price < 0) return null;
  const row: WeeklyPrice = {
    variationId: asId(raw['variationId']) ?? '',
    dayOfWeek,
    price,
    active: asBoolean(raw['active'], true),
  };
  const createdAt = asText(raw['createdAt']);
  if (createdAt !== null) row.createdAt = createdAt;
  const updatedAt = asText(raw['updatedAt']);
  if (updatedAt !== null) row.updatedAt = updatedAt;
  return row;
}

/**
 * Parse one raw per-date price (DNKAPI-0168), or null when unusable — a missing/blank
 * `date` or a missing/negative `price` drops the entry. `active`/timestamps as
 * {@link parseWeeklyPrice}.
 */
function parseDatePrice(raw: unknown): DatePrice | null {
  if (!isObject(raw)) return null;
  const date = asText(raw['date']);
  if (date === null) return null;
  const price = asNumber(raw['price']);
  if (price === null || price < 0) return null;
  const row: DatePrice = {
    variationId: asId(raw['variationId']) ?? '',
    date,
    price,
    active: asBoolean(raw['active'], true),
  };
  const createdAt = asText(raw['createdAt']);
  if (createdAt !== null) row.createdAt = createdAt;
  const updatedAt = asText(raw['updatedAt']);
  if (updatedAt !== null) row.updatedAt = updatedAt;
  return row;
}

/**
 * Parse the server-resolved effective price (DNKAPI-0168), or null when there is no
 * usable `price` (the surface isn't live / the body is empty — the caller degrades to
 * base). `source` is coerced to the known union, defaulting to `'base'` when absent or
 * unrecognized. Precedence is NEVER computed here — this only reads the server's value.
 */
function parseEffectivePrice(raw: unknown): EffectivePrice | null {
  if (!isObject(raw)) return null;
  const price = asNumber(raw['price']);
  if (price === null || price < 0) return null;
  const rawSource = asText(raw['source']);
  const source: EffectivePrice['source'] =
    rawSource === 'date' || rawSource === 'staff' || rawSource === 'weekly'
      ? rawSource
      : 'base';
  return { price, source };
}

/**
 * Tolerantly extract an id array from an assignment GET. Accepts a bare array of ids,
 * `{ <key> }` / `{ content }` of ids, or objects carrying `<idField>`/`id`. De-dupes,
 * order-preserving.
 */
function extractIds(body: unknown, key: string, idField: string): string[] {
  let raw: unknown[] = [];
  if (Array.isArray(body)) raw = body;
  else if (isObject(body)) {
    if (Array.isArray(body[key])) raw = body[key] as unknown[];
    else if (Array.isArray(body['content'])) raw = body['content'];
  }
  const ids: string[] = [];
  for (const entry of raw) {
    // Assignment fk ids are v2 entity ids — numeric on the wire (DNKADM-0064).
    const id =
      asId(entry) ??
      (isObject(entry) ? asId(entry[idField]) ?? asId(entry['id']) : null);
    if (id !== null && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** Tolerantly extract a list array (bare array or `{ <key> }`/`{ content }`). */
function extractList(body: unknown, key: string): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body[key])) return body[key] as unknown[];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

/** Build the JSON body for a service create/update, omitting absent optional keys. */
function toServiceBody(input: ServiceInput): Record<string, unknown> {
  const json: Record<string, unknown> = {
    name: input.name,
    staffAssignment: input.staffAssignment,
    resourceAssignment: input.resourceAssignment,
    taxable: input.taxable,
    // Confirmation workflow (DNKAPI-0278) — required-with-default; always sent.
    requiresConfirmation: input.requiresConfirmation,
    // Guest policy (DNKAPI-0145) — both required-with-defaults; always sent (`0` = unlimited).
    allowGuests: input.allowGuests,
    maxGuests: input.maxGuests,
    // Payment capture (DNKAPI-0271) — a concrete value is always in `input` (form-defaulted
    // to `charge`); only the two known values are sent.
    paymentMode: input.paymentMode,
    creditBucketId: input.creditBucketId,
  };
  if (input.description !== undefined) json['description'] = input.description;
  if (input.imageUrl !== undefined) json['imageUrl'] = input.imageUrl;
  // Preserve-on-null pair (DNKADM-0267): the keys go out ONLY with an actual
  // selection — an omitted key preserves the stored value server-side.
  if (input.variationDisplay !== undefined) json['variationDisplay'] = input.variationDisplay;
  if (input.featured !== undefined) json['featured'] = input.featured;
  // Booking lead time override (DNKADM-0385): 0 = clear/inherit, 1..MAX = set; an
  // omitted key leaves the stored value unchanged. Never null.
  if (input.leadTimeMinutes !== undefined) json['leadTimeMinutes'] = input.leadTimeMinutes;
  return json;
}

/** Build the JSON body for a variation create/update. */
function toVariationBody(input: ServiceVariationInput): Record<string, unknown> {
  return {
    name: input.name,
    isDefault: input.isDefault,
    durationMinutes: input.durationMinutes,
    preBufferMinutes: input.preBufferMinutes,
    postBufferMinutes: input.postBufferMinutes,
    price: input.price,
    noShowFee: input.noShowFee,
    creditCost: input.creditCost,
  };
}

// --- Path helpers --------------------------------------------------------------

function servicePath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

function copyServicePath(id: string): string {
  return `${servicePath(id)}/copy`;
}

function variationsPath(serviceId: string): string {
  return `${servicePath(serviceId)}/variations`;
}

function variationPath(serviceId: string, vid: string): string {
  return `${variationsPath(serviceId)}/${encodeURIComponent(vid)}`;
}

function copyVariationPath(serviceId: string, vid: string): string {
  return `${variationPath(serviceId, vid)}/copy`;
}

function staffPricesPath(serviceId: string, vid: string): string {
  return `${variationPath(serviceId, vid)}/staff-prices`;
}

function weeklyPricesPath(serviceId: string, vid: string): string {
  return `${variationPath(serviceId, vid)}/weekly-prices`;
}

function datePricesPath(serviceId: string, vid: string): string {
  return `${variationPath(serviceId, vid)}/date-prices`;
}

// --- Entity endpoints (U1) -----------------------------------------------------

/**
 * `GET /v2/admin/services?status=…` → the tenant's services, defensively parsed (a
 * bad entry dropped). The `status` filter (§4.3) defaults to `active` (live rows
 * only); pass `status: 'inactive'|'all'` or `includeInactive: true` to include
 * deactivated rows. Throws a typed `ApiError` on a non-2xx response.
 */
export async function listServices(options: ListServicesOptions = {}): Promise<V2Service[]> {
  const status = options.status ?? (options.includeInactive ? 'all' : 'active');
  const body = await request<unknown>('GET', PATH, { query: { status } });
  return (extractServices(body) ?? [])
    .map(parseService)
    .filter((s): s is V2Service => s !== null);
}

/**
 * `GET /v2/admin/services/{id}` → one service (defensively parsed). Throws a typed
 * `ApiError` (`404` NOT_FOUND, upstream forwarded).
 */
export async function getService(id: string): Promise<V2Service | null> {
  const body = await request<unknown>('GET', servicePath(id));
  return parseService(body);
}

/**
 * `POST /v2/admin/services` → create a service. Returns the re-parsed created row.
 * Throws a typed `ApiError` (`400` VALIDATION inline-able, `409` CONFLICT on the
 * unique `(tenant, name)` rule, upstream).
 */
export async function createService(input: ServiceInput): Promise<V2Service | null> {
  const body = await request<unknown>('POST', PATH, { json: toServiceBody(input) });
  return parseService(body);
}

/**
 * `PUT /v2/admin/services/{id}` → update a service. Returns the re-parsed updated row.
 * Throws a typed `ApiError`.
 */
export async function updateService(id: string, input: ServiceInput): Promise<V2Service | null> {
  const body = await request<unknown>('PUT', servicePath(id), { json: toServiceBody(input) });
  return parseService(body);
}

/**
 * `POST /v2/admin/services/{id}/deactivate` → soft-deactivate a service (§4.3 — never
 * a hard delete). Throws a typed `ApiError`.
 */
export async function deactivateService(id: string): Promise<void> {
  await request<unknown>('POST', `${servicePath(id)}/deactivate`);
}

/**
 * `POST /v2/admin/services/{id}/reactivate` → reactivate a soft-deactivated service
 * (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateService(id: string): Promise<void> {
  await request<unknown>('POST', `${servicePath(id)}/reactivate`);
}

/**
 * `POST /v2/admin/services/{id}/publish` → take a Draft service live. No body.
 * Idempotent — publishing an already-published service is a no-op that returns its
 * current state. Returns the re-parsed updated row. Throws a typed `ApiError`.
 */
export async function publishService(id: string): Promise<V2Service | null> {
  const body = await request<unknown>('POST', `${servicePath(id)}/publish`);
  return parseService(body);
}

/**
 * `POST /v2/admin/services/{id}/copy` → deep-clone a service (all four assignment sets
 * + every variation with its price overrides) into a new Draft. `name` is OPTIONAL: omit
 * it (the one-click path) to let the backend auto-generate a unique `"<source> (Copy)"`
 * name — no body is sent; supply `name` to set your own (a colliding supplied name
 * throws `409 DUPLICATE_NAME`). Returns the re-parsed NEW service (same shape as
 * create/get). Throws a typed `ApiError`.
 */
export async function copyService(id: string, name?: string): Promise<V2Service | null> {
  const body = await request<unknown>('POST', copyServicePath(id), {
    ...(name !== undefined ? { json: { name } } : {}),
  });
  return parseService(body);
}

// --- Assignment sub-resources (U2) ---------------------------------------------

/**
 * `GET /v2/admin/services/{id}/category-assignments` → the assigned
 * `v2_service_category.id`s (the categories tagged on the service). Tolerantly
 * extracts the id array. Throws a typed `ApiError`.
 */
export async function getServiceCategories(id: string): Promise<string[]> {
  const body = await request<unknown>('GET', `${servicePath(id)}/category-assignments`);
  return extractIds(body, 'categoryIds', 'categoryId');
}

/**
 * `PUT /v2/admin/services/{id}/category-assignments` ← `{ categoryIds }` → set the
 * WHOLE set of categories (the multi-select round-trips the full set). Throws a typed
 * `ApiError`.
 */
export async function setServiceCategories(id: string, categoryIds: string[]): Promise<void> {
  await request<unknown>('PUT', `${servicePath(id)}/category-assignments`, {
    json: { categoryIds },
  });
}

/**
 * `GET /v2/admin/services/{id}/location-assignments` → the `v2_location.id`s where the
 * service is offered. Tolerantly extracts the id array. Throws a typed `ApiError`.
 */
export async function getServiceLocations(id: string): Promise<string[]> {
  const body = await request<unknown>('GET', `${servicePath(id)}/location-assignments`);
  return extractIds(body, 'locationIds', 'locationId');
}

/**
 * `PUT /v2/admin/services/{id}/location-assignments` ← `{ locationIds }` → set the
 * WHOLE set of offered locations. The form enforces ≥1 before calling this. Throws a
 * typed `ApiError`.
 */
export async function setServiceLocations(id: string, locationIds: string[]): Promise<void> {
  await request<unknown>('PUT', `${servicePath(id)}/location-assignments`, {
    json: { locationIds },
  });
}

/**
 * `GET /v2/admin/services/{id}/staff-assignments` → the eligible `v2_staff.id`s.
 * Tolerantly extracts the id array. Throws a typed `ApiError`.
 */
export async function getServiceStaff(id: string): Promise<string[]> {
  const body = await request<unknown>('GET', `${servicePath(id)}/staff-assignments`);
  return extractIds(body, 'staffIds', 'staffId');
}

/**
 * `PUT /v2/admin/services/{id}/staff-assignments` ← `{ staffIds }` → set the WHOLE set
 * of eligible staff. Throws a typed `ApiError`.
 *
 * DNKAPI-0637 D-13: an EMPTY set is refused with `400 CLEAR_ALL_REQUIRED` unless the body
 * also carries `clearAll: true`, so one stray empty PUT can never wipe a service's coaches.
 * `clearAll` is sent ONLY when the caller passes `{ clearAll: true }` (the operator chose to
 * remove everyone) AND the set really is empty; a non-empty save never carries it.
 */
export async function setServiceStaff(
  id: string,
  staffIds: string[],
  opts: { clearAll?: boolean } = {},
): Promise<void> {
  const json: Record<string, unknown> = { staffIds };
  if (opts.clearAll === true && staffIds.length === 0) json['clearAll'] = true;
  await request<unknown>('PUT', `${servicePath(id)}/staff-assignments`, { json });
}

/**
 * `GET /v2/admin/services/{id}/resource-assignments` → the eligible `v2_resource.id`s.
 * Tolerantly extracts the id array. Throws a typed `ApiError`.
 */
export async function getServiceResources(id: string): Promise<string[]> {
  const body = await request<unknown>('GET', `${servicePath(id)}/resource-assignments`);
  return extractIds(body, 'resourceIds', 'resourceId');
}

/**
 * `PUT /v2/admin/services/{id}/resource-assignments` ← `{ resourceIds }` → set the
 * WHOLE set of eligible resources. Throws a typed `ApiError`.
 */
export async function setServiceResources(id: string, resourceIds: string[]): Promise<void> {
  await request<unknown>('PUT', `${servicePath(id)}/resource-assignments`, {
    json: { resourceIds },
  });
}

// --- Variations (U3) -----------------------------------------------------------

/**
 * `GET /v2/admin/services/{id}/variations` → the service's variations, defensively
 * parsed (a bad entry dropped). Throws a typed `ApiError`.
 */
export async function listVariations(serviceId: string): Promise<ServiceVariation[]> {
  const body = await request<unknown>('GET', variationsPath(serviceId));
  return extractList(body, 'variations')
    .map(parseVariation)
    .filter((v): v is ServiceVariation => v !== null);
}

/**
 * `POST /v2/admin/services/{id}/variations` → create a variation. Returns the
 * re-parsed created row. The caller enforces the REQUIRED `creditCost` + single-default
 * invariants before calling. Throws a typed `ApiError`.
 */
export async function createVariation(
  serviceId: string,
  input: ServiceVariationInput,
): Promise<ServiceVariation | null> {
  const body = await request<unknown>('POST', variationsPath(serviceId), {
    json: toVariationBody(input),
  });
  return parseVariation(body);
}

/**
 * `PUT /v2/admin/services/{id}/variations/{vid}` → update a variation. Returns the
 * re-parsed updated row. Throws a typed `ApiError`.
 */
export async function updateVariation(
  serviceId: string,
  vid: string,
  input: ServiceVariationInput,
): Promise<ServiceVariation | null> {
  const body = await request<unknown>('PUT', variationPath(serviceId, vid), {
    json: toVariationBody(input),
  });
  return parseVariation(body);
}

/**
 * `DELETE /v2/admin/services/{id}/variations/{vid}` → remove a variation. Throws a
 * typed `ApiError`. Refetch after it resolves.
 */
export async function deleteVariation(serviceId: string, vid: string): Promise<void> {
  await request<unknown>('DELETE', variationPath(serviceId, vid));
}

/**
 * `POST /v2/admin/services/{id}/variations/{vid}/copy` → deep-clone a variation
 * (DNKAPI-0170). The backend copies the base fields + ALL price exceptions (per-staff,
 * per-day-of-week, per-date) in this ONE transactional call — the FE never re-PUTs the
 * exceptions client-side. The copy is `isDefault:false`/`active:true` by contract (the
 * service keeps its one default — no default repair needed). Returns the re-parsed NEW
 * variation (same `ServiceVariation` shape as create/get — reuses {@link parseVariation}).
 *
 * `name` is OPTIONAL: omit it (the one-click path) to let the backend auto-generate a
 * unique `"<source> (Copy)"` name, and **no body is sent**; supply a `name` to set your
 * own — a colliding supplied name throws `409 DUPLICATE_NAME`. Throws a typed `ApiError`
 * (`404` NOT_FOUND for a foreign/unknown service or variation, `409` DUPLICATE_NAME,
 * upstream forwarded).
 */
export async function copyVariation(
  serviceId: string,
  vid: string,
  name?: string,
): Promise<ServiceVariation | null> {
  // Body sent ONLY when a name is supplied; the one-click path sends no body at all.
  const body = await request<unknown>('POST', copyVariationPath(serviceId, vid), {
    ...(name !== undefined ? { json: { name } } : {}),
  });
  return parseVariation(body);
}

// --- Per-staff variation price exceptions (U3) ---------------------------------

/**
 * `GET /v2/admin/services/{id}/variations/{vid}/staff-prices` → the per-staff money
 * overrides for the variation, defensively parsed (a bad entry dropped). Throws a
 * typed `ApiError`.
 */
export async function listVariationStaffPrices(
  serviceId: string,
  vid: string,
): Promise<VariationStaffPrice[]> {
  const body = await request<unknown>('GET', staffPricesPath(serviceId, vid));
  return extractList(body, 'staffPrices')
    .map(parseStaffPrice)
    .filter((p): p is VariationStaffPrice => p !== null);
}

/**
 * `PUT /v2/admin/services/{id}/variations/{vid}/staff-prices/{staffId}` ← `{ price }`
 * → upsert a per-staff money override (money-only; unique on `(staff, variation)` — the
 * id-in-path upsert enforces uniqueness). Returns the re-parsed row. Throws a typed
 * `ApiError`.
 */
export async function upsertVariationStaffPrice(
  serviceId: string,
  vid: string,
  staffId: string,
  price: number,
): Promise<VariationStaffPrice | null> {
  const body = await request<unknown>(
    'PUT',
    `${staffPricesPath(serviceId, vid)}/${encodeURIComponent(staffId)}`,
    { json: { price } },
  );
  return parseStaffPrice(body);
}

/**
 * `DELETE /v2/admin/services/{id}/variations/{vid}/staff-prices/{staffId}` → remove a
 * per-staff override (revert to the variation's base price). Throws a typed `ApiError`.
 */
export async function deleteVariationStaffPrice(
  serviceId: string,
  vid: string,
  staffId: string,
): Promise<void> {
  await request<unknown>(
    'DELETE',
    `${staffPricesPath(serviceId, vid)}/${encodeURIComponent(staffId)}`,
  );
}

// --- Per-day-of-week + per-date variation price overrides (DNKADM-0086) ---------
// Natural-key CRUD mirroring `staff-prices`: the key (dayOfWeek / date) lives in the
// PATH; the PUT body is just `{ price }`; PUT is an upsert (always 200, never 201);
// DELETE is a soft remove (204) → the key falls through to base. Money-only —
// `creditCost` is unaffected. Reads are parsed defensively (a bad entry is dropped).

/**
 * `GET /v2/admin/services/{id}/variations/{vid}/weekly-prices?status=…` → the
 * per-day-of-week money overrides for the variation, defensively parsed (a bad entry
 * dropped). `status` (`active|all`) is forwarded only when given. Throws a typed
 * `ApiError`.
 */
export async function getWeeklyPrices(
  serviceId: string,
  vid: string,
  status?: 'active' | 'all',
): Promise<WeeklyPrice[]> {
  const body = await request<unknown>('GET', weeklyPricesPath(serviceId, vid), {
    ...(status ? { query: { status } } : {}),
  });
  return extractList(body, 'weeklyPrices')
    .map(parseWeeklyPrice)
    .filter((p): p is WeeklyPrice => p !== null);
}

/**
 * `PUT /v2/admin/services/{id}/variations/{vid}/weekly-prices/{dayOfWeek}` ← `{ price }`
 * → upsert a per-day-of-week money override (the natural `(variation, dayOfWeek)` key
 * is in the PATH). Returns the re-parsed row. Throws a typed `ApiError` (`400` on a bad
 * day/price, inline-able).
 */
export async function putWeeklyPrice(
  serviceId: string,
  vid: string,
  dayOfWeek: number,
  price: number,
): Promise<WeeklyPrice | null> {
  const body = await request<unknown>(
    'PUT',
    `${weeklyPricesPath(serviceId, vid)}/${encodeURIComponent(String(dayOfWeek))}`,
    { json: { price } },
  );
  return parseWeeklyPrice(body);
}

/**
 * `DELETE /v2/admin/services/{id}/variations/{vid}/weekly-prices/{dayOfWeek}` → remove
 * a day-of-week override (the day reverts to base). Throws a typed `ApiError`.
 */
export async function deleteWeeklyPrice(
  serviceId: string,
  vid: string,
  dayOfWeek: number,
): Promise<void> {
  await request<unknown>(
    'DELETE',
    `${weeklyPricesPath(serviceId, vid)}/${encodeURIComponent(String(dayOfWeek))}`,
  );
}

/** Options for {@link getDatePrices} — `status` plus an optional `from`/`to` window. */
export interface GetDatePricesOptions {
  status?: 'active' | 'all';
  from?: string;
  to?: string;
}

/**
 * `GET /v2/admin/services/{id}/variations/{vid}/date-prices?status=…&from=…&to=…` → the
 * per-date money overrides for the variation, defensively parsed (a bad entry dropped).
 * `status`/`from`/`to` are forwarded only when given (all optional). Throws a typed
 * `ApiError`. (The date EDITOR is DNKADM-0086 Unit 2; the wrapper lands in Unit 1.)
 */
export async function getDatePrices(
  serviceId: string,
  vid: string,
  options: GetDatePricesOptions = {},
): Promise<DatePrice[]> {
  const query: Record<string, string> = {};
  if (options.status) query['status'] = options.status;
  if (options.from) query['from'] = options.from;
  if (options.to) query['to'] = options.to;
  const body = await request<unknown>('GET', datePricesPath(serviceId, vid), {
    ...(Object.keys(query).length > 0 ? { query } : {}),
  });
  return extractList(body, 'datePrices')
    .map(parseDatePrice)
    .filter((p): p is DatePrice => p !== null);
}

/**
 * `PUT /v2/admin/services/{id}/variations/{vid}/date-prices/{date}` ← `{ price }` →
 * upsert a per-date money override (the natural `(variation, date)` key is in the
 * PATH; `date` is `YYYY-MM-DD`). Returns the re-parsed row. Throws a typed `ApiError`.
 */
export async function putDatePrice(
  serviceId: string,
  vid: string,
  date: string,
  price: number,
): Promise<DatePrice | null> {
  const body = await request<unknown>(
    'PUT',
    `${datePricesPath(serviceId, vid)}/${encodeURIComponent(date)}`,
    { json: { price } },
  );
  return parseDatePrice(body);
}

/**
 * `DELETE /v2/admin/services/{id}/variations/{vid}/date-prices/{date}` → remove a
 * per-date override (the date falls through to the next precedence rung). Throws a
 * typed `ApiError`.
 */
export async function deleteDatePrice(
  serviceId: string,
  vid: string,
  date: string,
): Promise<void> {
  await request<unknown>(
    'DELETE',
    `${datePricesPath(serviceId, vid)}/${encodeURIComponent(date)}`,
  );
}

/**
 * TOLERANT effective-price read (DNKADM-0086) — the display surface is NOT locked yet
 * (DNKAPI-0168 is implementing). Tries
 * `GET /v2/admin/services/{id}/variations/{vid}/effective-price?date=…[&staffId=…]` and
 * parses `{ price, source }`. If the surface isn't live (404 / empty / any failure) it
 * **degrades to `null`** rather than throwing — the caller then shows the variation's
 * base price. Precedence is resolved SERVER-SIDE; this never computes it client-side.
 * (The DISPLAY consuming this is Unit 2; the wrapper lands in Unit 1.)
 */
export async function getEffectivePrice(
  serviceId: string,
  variationId: string,
  date: string,
  staffId?: string,
): Promise<EffectivePrice | null> {
  try {
    const query: Record<string, string> = { date };
    if (staffId) query['staffId'] = staffId;
    const body = await request<unknown>(
      'GET',
      `${variationPath(serviceId, variationId)}/effective-price`,
      { query },
    );
    return parseEffectivePrice(body);
  } catch {
    // Forward-compatible: an absent/erroring surface degrades to base (no throw).
    return null;
  }
}
